import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../services/api';

const ACCENT = '#4f46e5';
const GREEN  = '#10b981';

function useCountUp(target, duration, animKey) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    if (target === 0) { setVal(0); return; }
    const t0 = performance.now();
    const tick = now => {
      const p = Math.min((now - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * e));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, animKey]); // eslint-disable-line
  return val;
}

function eur(n) { return '€' + Number(n).toLocaleString('nl-NL'); }

export default function AdminPlatformAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('overview');
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    api.adminGetPlatformAnalytics()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (view === 'revenue') setAnimKey(k => k + 1);
  }, [view]);

  const rev = data?.revenue || { monthlyIncome:0, netRevenue:0, mrr:0, arr:0, monthlyRevenue:[], restaurants:[] };
  const cMonthly = useCountUp(rev.monthlyIncome, 2800, animKey);
  const cNet     = useCountUp(rev.netRevenue,    3000, animKey);
  const cMRR     = useCountUp(rev.mrr,           2400, animKey);
  const cARR     = useCountUp(rev.arr,           2600, animKey);

  if (loading) return <div style={{ color:'#64748b', padding:40 }}>Loading…</div>;
  if (!data)   return <div style={{ color:'#ef4444', padding:40 }}>Failed to load.</div>;

  const { totalRestaurants, activeRestaurants, totalReservations, totalCovers, topRestaurants, dailyPlatform } = data;

  const dailyMap = Object.fromEntries(dailyPlatform.map(d => [d.date, d]));
  const allDates = [];
  const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 29);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const k = d.toISOString().slice(0, 10);
    allDates.push({
      label: new Date(k+'T12:00:00Z').toLocaleDateString('en-US',{month:'short',day:'numeric',timeZone:'UTC'}),
      reservations: dailyMap[k]?.reservations || 0,
    });
  }
  const maxBookings = Math.max(...topRestaurants.map(r => r.bookings), 1);

  return (
    <div>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={s.h1}>Platform Analytics</h1>
          <p style={s.sub}>Overview across all restaurants.</p>
        </div>
        <div style={s.tabRow}>
          <button onClick={() => setView('overview')} style={{ ...s.tab, ...(view==='overview' ? s.tabOn : {}) }}>Overview</button>
          <button onClick={() => setView('revenue')}  style={{ ...s.tab, ...(view==='revenue'  ? s.tabOn : {}) }}>Revenue</button>
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      {view === 'overview' && <>
        <div style={s.grid4}>
          {[
            { label:'Total restaurants', value: totalRestaurants },
            { label:'Active',            value: activeRestaurants },
            { label:'Bookings (30d)',    value: totalReservations },
            { label:'Covers (30d)',      value: totalCovers },
          ].map(c => (
            <div key={c.label} style={s.card}>
              <div style={s.cardVal}>{c.value}</div>
              <div style={s.cardLbl}>{c.label}</div>
            </div>
          ))}
        </div>

        <div style={s.chartCard}>
          <div style={s.ct}>Bookings per day — all restaurants</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={allDates} margin={{top:4,right:8,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{fontSize:11,fill:'#94a3b8'}} interval={Math.floor(allDates.length/7)} />
              <YAxis tick={{fontSize:11,fill:'#94a3b8'}} allowDecimals={false} />
              <Tooltip contentStyle={s.tip} />
              <Bar dataKey="reservations" fill={ACCENT} radius={[4,4,0,0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={s.chartCard}>
          <div style={s.ct}>Most active restaurants (30d)</div>
          {topRestaurants.every(r => r.bookings === 0)
            ? <div style={{color:'#94a3b8',fontSize:14}}>No booking data yet.</div>
            : <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {topRestaurants.map((r,i) => (
                  <div key={r.name}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                      <span style={{fontSize:13,fontWeight:600,color:'#334155'}}>{r.name}</span>
                      <span style={{fontSize:12,color:'#94a3b8'}}>{r.bookings} bookings</span>
                    </div>
                    <div style={{height:6,background:'#f1f5f9',borderRadius:4,overflow:'hidden'}}>
                      <div style={{width:`${(r.bookings/maxBookings)*100}%`,height:'100%',background:ACCENT,borderRadius:4,opacity:1-i*0.1}} />
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      </>}

      {/* ── REVENUE ── */}
      {view === 'revenue' && <>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
          <div style={s.greenCard}>
            <div style={s.greenLbl}>Monthly income</div>
            <div style={s.greenVal}>{eur(cMonthly)}</div>
            <div style={s.greenSub}>Received this month</div>
          </div>
          <div style={s.greenCard}>
            <div style={s.greenLbl}>Net revenue</div>
            <div style={s.greenVal}>{eur(cNet)}</div>
            <div style={s.greenSub}>Total received — all time</div>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
          <div style={s.card}>
            <div style={s.cardVal}>{eur(cMRR)}</div>
            <div style={s.cardLbl}>MRR</div>
            <div style={s.cardSub}>Monthly recurring</div>
          </div>
          <div style={s.card}>
            <div style={s.cardVal}>{eur(cARR)}</div>
            <div style={s.cardLbl}>ARR</div>
            <div style={s.cardSub}>Annual recurring</div>
          </div>
          <div style={s.card}>
            <div style={s.cardVal}>{rev.restaurants?.filter(r=>r.is_active).length ?? activeRestaurants}</div>
            <div style={s.cardLbl}>Paying accounts</div>
            <div style={s.cardSub}>Active subscriptions</div>
          </div>
        </div>

        <div style={s.chartCard}>
          <div style={s.ct}>Monthly revenue</div>
          {rev.monthlyRevenue.length === 0
            ? <div style={{color:'#94a3b8',fontSize:14,padding:'20px 0'}}>No payment data yet. Billing will populate this.</div>
            : <ResponsiveContainer width="100%" height={200}>
                <BarChart data={rev.monthlyRevenue} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{fontSize:11,fill:'#94a3b8'}} />
                  <YAxis tick={{fontSize:11,fill:'#94a3b8'}} tickFormatter={v=>`€${v}`} />
                  <Tooltip contentStyle={s.tip} formatter={v=>[`€${v}`,'Revenue']} />
                  <Bar dataKey="revenue" fill={GREEN} radius={[4,4,0,0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
          }
        </div>

        <div style={s.chartCard}>
          <div style={s.ct}>Per restaurant</div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr>{['Restaurant','Monthly price','Status'].map(h =>
                <th key={h} style={s.th}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {rev.restaurants?.length > 0
                ? rev.restaurants.map((r,i) => (
                    <tr key={i}>
                      <td style={{...s.td,fontWeight:600,color:'#0f172a'}}>{r.name}</td>
                      <td style={s.td}>€{r.monthly_price}</td>
                      <td style={s.td}>
                        <span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:5,
                          background: r.is_active ? '#f0fdf4':'#f1f5f9',
                          color:      r.is_active ? '#15803d':'#94a3b8'}}>
                          {r.is_active ? 'Active':'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                : <tr><td colSpan={3} style={{...s.td,color:'#94a3b8',textAlign:'center',padding:'28px'}}>No restaurants yet.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
}

const s = {
  h1:      { fontSize:28, fontWeight:800, color:'#0f172a', margin:0, letterSpacing:'-0.5px' },
  sub:     { fontSize:14, color:'#64748b', marginTop:4 },

  tabRow:  { display:'flex', background:'#f1f5f9', borderRadius:10, padding:3 },
  tab:     { padding:'7px 16px', borderRadius:7, border:'none', background:'none', fontSize:13, color:'#64748b', cursor:'pointer', fontWeight:500 },
  tabOn:   { background:'#fff', color:'#0f172a', fontWeight:700, boxShadow:'0 1px 4px rgba(15,23,42,0.08)' },

  grid4:   { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 },
  card:    { background:'#fff', border:'1px solid #e2e8f0', borderRadius:16, padding:'18px 20px', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  cardVal: { fontSize:28, fontWeight:800, color:'#0f172a', marginBottom:4 },
  cardLbl: { fontSize:12, color:'#64748b', fontWeight:500 },
  cardSub: { fontSize:11, color:'#94a3b8', marginTop:2 },

  greenCard: { background:'linear-gradient(135deg,#064e3b 0%,#065f46 100%)', borderRadius:18, padding:'28px 30px', boxShadow:'0 4px 20px rgba(16,185,129,0.2)' },
  greenLbl:  { fontSize:11, fontWeight:700, color:'rgba(167,243,208,0.8)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 },
  greenVal:  { fontSize:42, fontWeight:900, color:'#6ee7b7', letterSpacing:'-1px', lineHeight:1 },
  greenSub:  { fontSize:12, color:'rgba(167,243,208,0.6)', marginTop:8 },

  chartCard: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:18, padding:'20px 24px', marginBottom:16, boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  ct:        { fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 },
  tip:       { background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, fontSize:13 },

  th: { padding:'8px 12px', fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' },
  td: { padding:'11px 12px', fontSize:13, color:'#475569', borderBottom:'1px solid #f1f5f9' },
};
